import {
  Box,
  Label,
  Dialog,
  Text as PrimerText
} from "@primer/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { lichSuXuongPhongYTeApi } from "../../../api/lichSuXuongPhongYTeApi";
import DataTable from "../../../components/ui/data-table";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { ILichSuXuongPhongYTe } from "../../../models/response/crm-hocsinh/ILichSuXuongPhongYTe";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import HocSinhShortInfo from "../../../components/hoc-sinh/HocSinhShortInfo";
import HocSinhProfile from "../HocSinhProfile";

interface LichSuXuongPhongYTeProps {
  hocSinhId: number;
  canEdit?: boolean;
}

const LichSuXuongPhongYTePage: React.FC<LichSuXuongPhongYTeProps> = ({ hocSinhId, canEdit = true }) => {
  const { nam_hoc } = useCommonSelectedHook();
  const [namHocSelected, setNamHocSelected] = useState<string>("");
  const [listXuongPhongYTe, setListXuongPhongYTe] = useState<ILichSuXuongPhongYTe[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isShowHocSinhProfileModal, setIsShowHocSinhProfileModal] = useState(false);
  const [selectedHocSinhId, setSelectedHocSinhId] = useState<number>(0);

  useEffect(() => {
    setNamHocSelected(nam_hoc);
  }, [nam_hoc]);

  useEffect(() => {
    if (namHocSelected && hocSinhId) {
      getLLichSuXuongPhongYTe();
    }
  }, [namHocSelected, hocSinhId]);

  const getLLichSuXuongPhongYTe = async () => {
    try {
      const res = await lichSuXuongPhongYTeApi.selectByHocSinh(
          hocSinhId
        );
      if (res.is_success && res.data) {
        setListXuongPhongYTe(res.data);
      } else {
        setListXuongPhongYTe([]);
      }
    } catch (error) {
      console.error("Error fetching mien giam list:", error);
      setListXuongPhongYTe([]);
    }
  };

  return (
    <Box sx={{ paddingTop: 15 }}>
      <DataTable
        height={`${window.innerHeight - 200}px`}
        actionComponent={
          <Box
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
          
          </Box>
        }
        columns={[
          {
            dataField: "nhom_noi_dung",
            width: "150px",
            caption: `Loại`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  <TextWithEllipse text={data.nhom_noi_dung || "-"} lineNumber={2} />
                  {/* Hiển thị loại dặn thuốc nếu là dặn thuốc */}
                  {data.nhom_noi_dung === "Dặn thuốc" && data.fa_danthuoc_type_ten && (
                    <Box sx={{
                      mt: 0.5,
                      fontSize: "11px",
                      color: "accent.fg",
                      fontStyle: "italic"
                    }}>
                      ({data.fa_danthuoc_type_ten})
                    </Box>
                  )}
                </Box>
              );
            },
          },
          {
            dataField: "trang_thai",
            width: 120,
            align: "center",
            caption: "Trạng thái",
            fixed: true,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              const trangThai = data.trang_thai || "";

              // Xác định variant và màu sắc dựa trên trạng thái
              let variant: "default" | "primary" | "secondary" | "accent" | "success" | "attention" | "severe" | "danger" | "done" | "sponsors" = "default";

              switch (trangThai.toLowerCase()) {
                case "đã hoàn thành":
                case "hoàn thành":
                  variant = "success";
                  break;
                case "từ chối":
                  variant = "danger";
                  break;
                case "đang xử lý":
                case "chờ xử lý":
                  variant = "attention";
                  break;
                case "tạo mới":
                  variant = "primary";
                  break;
                default:
                  variant = "default";
              }

              return (
                <Label variant={variant} size="small">
                  {trangThai || "Chưa xác định"}
                </Label>
              );
            }
          },        
          {
            dataField: "ngay_gio_kham",
            width: "140px",
            caption: `Ngày giờ khám`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                  {data.ngay_gio_kham ? moment(data.ngay_gio_kham).format("DD/MM/YY HH:mm") : "-"}
                </Box>
              );
            },
          },
          {
            dataField: "ngay_gio_tra_ve",
            width: "140px",
            caption: `Ngày giờ trả về`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                  {data.ngay_gio_tra_ve ? moment(data.ngay_gio_tra_ve).format("DD/MM/YY HH:mm") : "-"}
                </Box>
              );
            },
          },
          {
            dataField: "ly_do_xuong_phong_y_te",
            width: "200px",
            caption: `Lý do xuống phòng y tế`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  <TextWithEllipse text={data.ly_do_xuong_phong_y_te || "-"} lineNumber={2} allowToggleText={true} />
                </Box>
              );
            },
          },
          {
            dataField: "dien_bien_tinh_trang",
            width: "200px",
            caption: `Diễn biến tình trạng`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  <TextWithEllipse text={data.tinh_trang || "-"} lineNumber={2} allowToggleText={true} />
                </Box>
              );
            },
          },
          {
            dataField: "xu_tri_y_te_voi_hoc_sinh",
            width: "200px",
            caption: `Xử trí y tế`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  <TextWithEllipse text={data.xu_tri_y_te_voi_hoc_sinh || "-"} lineNumber={2} allowToggleText={true} />
                </Box>
              );
            },
          },
          {
            id: "lien_he_info",
            width: "180px",
            caption: `Liên hệ`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  {data.lien_he_phhs && (
                    <Box sx={{ mb: 0.5 }}>
                      <Box sx={{ fontWeight: 600, color: "accent.fg" }}>PHHS:</Box>
                      <TextWithEllipse text={data.lien_he_phhs} lineNumber={1} />
                    </Box>
                  )}
                  {data.lien_he_gvcn && (
                    <Box>
                      <Box sx={{ fontWeight: 600, color: "accent.fg" }}>GVCN:</Box>
                      <TextWithEllipse text={data.lien_he_gvcn} lineNumber={1} />
                    </Box>
                  )}
                  {!data.lien_he_phhs && !data.lien_he_gvcn && "-"}
                </Box>
              );
            },
          },
          {
            dataField: "chuyen_co_so_y_te",
            width: "180px",
            caption: `Chuyển cơ sở y tế`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  <TextWithEllipse text={data.chuyen_co_so_y_te || "-"} lineNumber={2} allowToggleText={true} />
                </Box>
              );
            },
          },
          {
            dataField: "truong_hop_dac_biet",
            width: "180px",
            caption: `Trường hợp đặc biệt`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ fontSize: "12px" }}>
                  <TextWithEllipse text={data.truong_hop_dac_biet || "-"} lineNumber={2} allowToggleText={true} />
                </Box>
              );
            },
          },
          {
            dataField: "ngay_tao",
            width: "100px",
            caption: `Ngày tạo`,
            cellRender: (data: ILichSuXuongPhongYTe) => {
              return (
                <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                  {moment(data.ngay_tao).format("DD/MM/YY") || ""}
                </Box>
              );
            },
          },
        ]}
        data={listXuongPhongYTe}
        title="Lịch sử xuống phòng y tế"
        searchEnable
      />
      {/* Modal HocSinhProfile */}
      {isShowHocSinhProfileModal && selectedHocSinhId > 0 && (
        <HocSinhProfile
          id={selectedHocSinhId}
          onClose={() => {
            setIsShowHocSinhProfileModal(false);
          }}
          onSuccess={() => {
            setIsShowHocSinhProfileModal(false);
          }}
        />
      )}

      {/* Modal preview ảnh */}
      {previewImage && (
        <Dialog
          isOpen={!!previewImage}
          onDismiss={() => setPreviewImage(null)}
          aria-labelledby="preview-image-title"
        >
          <Dialog.Header id="preview-image-title">
            Xem ảnh
          </Dialog.Header>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default LichSuXuongPhongYTePage;
